import { catalog, plants } from "../data/catalog";
import { careOrder } from "../data/taxonomy";
import type { BalconyProfile, CatalogItem, Plant, PlanTemplate } from "../data/model";
import { planTemplates } from "../data/plans";

export const isPlantCompatible = (plant: Plant, profile: BalconyProfile) => {
  if (!plant.lights.includes(profile.light)) return false;
  if (careOrder[plant.care] > careOrder[profile.care]) return false;
  if (profile.hasPets && plant.petSafety !== "safe") return false;
  if (profile.hasKids && plant.childSafety === "unsafe") return false;
  if (profile.wind === "windy" && plant.windTolerance < 2) return false;
  return true;
};

export const isCatalogCompatible = (item: CatalogItem, profile: BalconyProfile) => {
  if (item.type === "plant") return isPlantCompatible(item, profile);
  if (item.type === "pot") {
    if ((profile.hasKids || profile.wind === "windy") && item.hanging) return false;
    return item.depthCm <= profile.depthCm * 0.65;
  }
  if (item.type === "furniture") return profile.depthCm >= item.minDepthCm;
  if (profile.wind === "windy" && !item.windSafe) return false;
  return profile.depthCm >= item.minDepthCm;
};

export const compatibleCatalog = (profile: BalconyProfile) =>
  catalog.filter((entry) => isCatalogCompatible(entry, profile));

const fallbackPlant = (profile: BalconyProfile, styleTags: string[]) =>
  plants
    .filter((plant) => isPlantCompatible(plant, profile))
    .sort((a, b) => {
      const score = (plant: Plant) =>
        plant.styleTags.filter((tag) => styleTags.includes(tag)).length * 10 -
        plant.unitPrice / 100000;
      return score(b) - score(a);
    })[0];

export const chooseTemplate = (profile: BalconyProfile): PlanTemplate => {
  const styleMatch = planTemplates.find((template) => template.styleId === profile.styleId);
  const lightMatch = planTemplates.find((template) => template.supportedLights.includes(profile.light));
  return styleMatch ?? lightMatch ?? planTemplates[0];
};

export const generatePlan = (profile: BalconyProfile) => {
  const template = chooseTemplate(profile);
  const styleTags = template.styleId === "edible-wood" ? ["mộc", "ăn được"] : template.styleId === "quiet-shade" ? ["râm", "texture"] : ["nhiệt đới", "rủ"];
  const used = new Set<string>();
  return template.items
    .map((item) => {
      const record = catalog.find((entry) => entry.id === item.refId);
      if (!record) return null;
      if (isCatalogCompatible(record, profile)) {
        used.add(item.instanceId);
        return { ...item };
      }
      if (record.type === "plant") {
        const replacement = fallbackPlant(profile, styleTags);
        if (!replacement) return null;
        used.add(item.instanceId);
        return { ...item, refId: replacement.id };
      }
      return null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .slice(0, 12)
    .map((item, index) => ({ ...item, instanceId: `${template.id}-${index + 1}` }));
};

export const noFitReasons = (profile: BalconyProfile) => {
  const reasons: string[] = [];
  if (profile.widthCm < 140) reasons.push("chiều rộng dưới 140 cm");
  if (profile.depthCm < 70) reasons.push("chiều sâu dưới 70 cm");
  if ((profile.widthCm * profile.depthCm) / 10000 < 1.2) reasons.push("diện tích dưới 1,2 m²");
  return reasons;
};
